import type { ApolloClient, RefetchQueriesInclude } from '@apollo/client';
import type { FinderRefetchOptions } from '@frameio/shared/contexts/FinderRefetchContext';
import {
  GetTrashAssets,
  type GetTrashAssetsQuery,
  type GetTrashAssetsQueryVariables,
} from '@frameio/trash/gql/generated/GetTrashAssets';
import { getTrashAssets } from '@frameio/trash/utils/getTrashAssets';
import { getIsOrderPossiblyChanged } from './getIsOrderPossiblyChanged';
import type { GetTrashAssetsObservableQuery } from './shared';

type AssetType = GetTrashAssetsQueryVariables['query']['assetType'];

/**
 * getRefetchTrashAssetsOptions
 *
 * This utility function returns refetch query options for Finder 2 assets.
 *
 * @param {ApolloClient} client  Apollo client
 * @param {FinderRefetchOptions} opts Refetch options
 * @returns {RefetchQueriesOptions}
 */
export const getRefetchTrashAssetsOptions = (
  client: ApolloClient<any>,
  observedQueryNames: Set<string>,
  activeFolderId: string,
  opts?: FinderRefetchOptions
) => {
  const include: RefetchQueriesInclude = [];
  observedQueryNames.forEach((queryName) => {
    if (queryName === 'GetTrashAssets') {
      include.push(GetTrashAssets);
    }
  });
  return {
    include,
    onQueryUpdated: async (query: GetTrashAssetsObservableQuery) => {
      const variables = query.variables;
      if (
        opts?.changedFieldDefinitionIds &&
        !getIsOrderPossiblyChanged(query, opts.changedFieldDefinitionIds)
      ) {
        return false;
      }

      if (
        !variables ||
        (activeFolderId && variables.query.folderId !== activeFolderId) ||
        !(variables.query.sortBy || variables.query.deletedAtSort)
      ) {
        return false;
      }

      const trashAssets = getTrashAssets(query.getCurrentResult().data);
      const fetchedNodes = trashAssets?.nodes ?? [];

      const refetchedNodes: NonNullable<typeof trashAssets>['nodes'] = [];

      const shouldContinueFetching = (
        assetType: AssetType,
        hasNextPage?: boolean
      ) => {
        if (assetType === 'FOLDER' && hasNextPage === false) {
          return true;
        }

        //if there aren't groups we need to fetch until we have at least the number of nodes we started with
        return hasNextPage && refetchedNodes.length < fetchedNodes?.length;
      };

      const fetchNextPage = async (
        assetType: AssetType = 'FOLDER',
        after: string | null = null
      ) => {
        return client.query<GetTrashAssetsQuery, GetTrashAssetsQueryVariables>({
          query: query.query,
          fetchPolicy: 'network-only',
          variables: {
            ...variables,
            query: {
              ...variables.query,
              assetType,
            },
            page: {
              first: 1000,
              after,
            },
          },
        });
      };

      const fetchSequentially = async (
        assetType: AssetType = 'FOLDER',
        after: string | null = null
      ) => {
        const { data } = await fetchNextPage(assetType, after);
        const nextTrashAssets = getTrashAssets(data);

        if (!nextTrashAssets?.nodes) return;

        refetchedNodes.push(...nextTrashAssets.nodes);

        const pageInfo = nextTrashAssets.pageInfo;
        const switchToFiles =
          assetType === 'FOLDER' && pageInfo.hasNextPage === false;
        const nextAssetType: AssetType = switchToFiles ? 'FILE' : 'FOLDER';
        const nextCursor = switchToFiles ? null : pageInfo.endCursor;

        if (shouldContinueFetching(assetType, pageInfo?.hasNextPage ?? false)) {
          await fetchSequentially(nextAssetType, nextCursor);
        }
      };

      fetchSequentially();
      return false;
    },
  };
};
